import { useState, useRef } from "react";
import { matchSpecies, stripSpecies } from "@/lib/species";
import { NoteArea } from "./NoteArea";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, X, Check, Camera, Trash2, ImageIcon, Copy, FolderOpen, ChevronDown } from "lucide-react";
import { resolveImageUrl } from "@/lib/api";

const emptyLumber = () => [
  { length: "8", quantity: "", description: "Poplar", thickness: "3.5", width: "6" },
  { length: "8", quantity: "", description: "Poplar", thickness: "3.5", width: "6" },
  { length: "8", quantity: "", description: "Poplar", thickness: "3.5", width: "6" },
];

function ComponentFormRow({ comp, idx, updateComponent, updateLumber, removeComponent, canRemove, onDuplicateComponent }) {
  const isStringer = comp.name.toLowerCase().includes('stringer');

  return (
    <div className="p-3 border-2 border-zinc-500 bg-zinc-50 rounded-sm" data-testid={`component-row-${idx}`}>
      <div className="mb-2">
        <p className="text-xs uppercase tracking-[0.2em] font-black text-zinc-700 text-center">Component {idx + 1}</p>
        {(comp.name || "").toLowerCase().includes("nail") && (
          <p className="text-[10px] text-amber-700 font-semibold text-center mt-0.5" data-testid={`component-no-cutlist-note-${idx}`}>
            This Component Does Not Get Added To Cut List
          </p>
        )}
        {onDuplicateComponent && (
          <div className="flex justify-center mt-1">
            <button
              type="button"
              onClick={() => onDuplicateComponent(idx)}
              className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-2 py-1 transition-colors"
              data-testid={`duplicate-component-btn-${idx}`}
            >
              <Copy className="w-3 h-3" />
              Duplicate Component From
            </button>
          </div>
        )}
      </div>
      <div className="flex gap-2 items-center mb-2">
        <div className="w-1.5 h-8 bg-orange-500 rounded-full flex-shrink-0" />
        <Input
          value={comp.name}
          maxLength={50}
          onChange={(e) => updateComponent(idx, "name", e.target.value)}
          placeholder="Component name"
          className="flex-1 bg-white border border-zinc-300 rounded-none shadow-none focus-visible:ring-2 focus-visible:ring-orange-600"
          data-testid={`component-name-input-${idx}`}
        />
        <Input
          type="number"
          step="0.01"
          min="0"
          value={comp.quantity}
          maxLength={15}
          onChange={(e) => updateComponent(idx, "quantity", e.target.value.slice(0, 15))}
          placeholder="Qty"
          className="w-20 bg-white border border-zinc-300 rounded-none shadow-none focus-visible:ring-2 focus-visible:ring-orange-600"
          data-testid={`component-qty-input-${idx}`}
        />
        {canRemove && (
          <button
            type="button"
            onClick={() => removeComponent(idx)}
            className="p-2 hover:bg-red-100 transition-colors"
            data-testid={`remove-component-btn-${idx}`}
          >
            <X className="w-4 h-4 text-red-600" />
          </button>
        )}
      </div>
      <div className="mt-2">
        <div className="p-2 bg-blue-50 border border-blue-200" data-testid={`component-dimensions-${idx}`}>
          <Label className="text-xs font-semibold text-blue-700 mb-2 block uppercase tracking-wide">Dimensions</Label>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs text-blue-600 mb-1 block">Length</Label>
              <Input type="number" step="0.125" min="0" value={comp.length} maxLength={15}
                onChange={(e) => updateComponent(idx, "length", e.target.value.slice(0, 15))}
                placeholder="0" className="bg-white border border-blue-300 rounded-none shadow-none focus-visible:ring-2 focus-visible:ring-blue-500 text-center"
                data-testid={`component-length-input-${idx}`} />
            </div>
            <div>
              <Label className="text-xs text-blue-600 mb-1 block">Width</Label>
              <Input type="number" step="0.125" min="0" value={comp.height} maxLength={15}
                onChange={(e) => updateComponent(idx, "height", e.target.value.slice(0, 15))}
                placeholder="0" className="bg-white border border-blue-300 rounded-none shadow-none focus-visible:ring-2 focus-visible:ring-blue-500 text-center"
                data-testid={`component-height-input-${idx}`} />
            </div>
            <div>
              <Label className="text-xs text-blue-600 mb-1 block">Thick</Label>
              <Input type="number" step="0.125" min="0" value={comp.thickness} maxLength={15}
                onChange={(e) => updateComponent(idx, "thickness", e.target.value.slice(0, 15))}
                placeholder="0" className="bg-white border border-blue-300 rounded-none shadow-none focus-visible:ring-2 focus-visible:ring-blue-500 text-center"
                data-testid={`component-thickness-input-${idx}`} />
            </div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-2">
        <div className="col-span-2 p-2 bg-amber-50 border border-amber-200">
          <Label className="text-xs font-semibold text-amber-700 mb-2 block uppercase tracking-wide">Time & Material</Label>
          <div className="grid grid-cols-2 gap-2 items-end">
            <div>
              <Label className="text-xs text-amber-600 mb-1 block">Pcs/Min</Label>
              <Input type="number" step="0.5" min="0" value={comp.pcs_per_min} maxLength={15}
                onChange={(e) => updateComponent(idx, "pcs_per_min", e.target.value.slice(0, 15))}
                placeholder="0" className="bg-white border border-amber-300 rounded-none shadow-none focus-visible:ring-2 focus-visible:ring-amber-500 text-center"
                data-testid={`component-pcs-per-min-input-${idx}`} />
            </div>
            <div>
              <button type="button"
                onClick={() => {
                  const L = parseFloat(comp.length), W = parseFloat(comp.height), T = parseFloat(comp.thickness);
                  if (L > 0 && W > 0 && T > 0) {
                    const bf = String(parseFloat(((L * W * T) / 144).toFixed(4))).slice(0, 7);
                    updateComponent(idx, "board_footage", bf);
                  }
                }}
                className="text-[10px] font-bold uppercase tracking-wide text-amber-700 bg-amber-100 border border-amber-300 px-1.5 py-0.5 hover:bg-amber-200 transition-colors mb-1"
                data-testid={`component-bdft-autofill-${idx}`}>
                Auto-Fill
              </button>
              <Label className="text-xs text-amber-600 mb-1 block leading-tight">Finished Product<br />Bd Ft Per Piece</Label>
              <Input type="number" step="0.01" min="0" value={comp.board_footage} maxLength={15}
                onChange={(e) => updateComponent(idx, "board_footage", e.target.value.slice(0, 15))}
                placeholder="0" className="bg-white border border-amber-300 rounded-none shadow-none focus-visible:ring-2 focus-visible:ring-amber-500 text-center"
                data-testid={`component-board-footage-input-${idx}`} />
            </div>
          </div>
        </div>
        <div className="col-span-2 p-2 bg-cyan-50 border border-cyan-200">
          <Label className="text-xs font-semibold text-cyan-700 mb-2 block uppercase tracking-wide">Boards/Skid</Label>
          <Input type="number" step="1" min="0" value={comp.skids} maxLength={15}
            onChange={(e) => updateComponent(idx, "skids", e.target.value.slice(0, 15))}
            placeholder="e.g. 1078" className="bg-white border border-cyan-300 rounded-none shadow-none focus-visible:ring-2 focus-visible:ring-cyan-500 text-center font-semibold"
            data-testid={`component-skids-input-${idx}`} />
        </div>
      </div>
      <div className="mt-2 p-2 bg-violet-50 border border-violet-200" data-testid={`component-lumber-section-${idx}`}>
        <Label className="text-xs font-semibold text-violet-700 mb-2 block uppercase tracking-wide text-center">Lumber Options</Label>
        <div className="space-y-3">
          {(comp.lumber || emptyLumber()).map((lbr, lbrIdx) => (
            <div key={lbrIdx} className="space-y-1.5 border-2 border-violet-500 p-2 bg-white/60">
              <p className="text-[10px] font-bold text-violet-700 uppercase tracking-wider">Lumber Option {lbrIdx + 1}</p>
              {/* Row 1: Thickness, Width, Length */}
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs text-violet-600 mb-1 block">Thickness</Label>
                  <select value={lbr.thickness || "3.5"} onChange={(e) => updateLumber(idx, lbrIdx, "thickness", e.target.value)}
                    className="w-full h-9 border border-violet-300 bg-white rounded-none text-sm px-1 focus:ring-2 focus:ring-violet-500 focus:outline-none"
                    data-testid={`component-lumber-thick-${lbrIdx}-input-${idx}`}>
                    <option value="">—</option>
                    <option value="1.5">1.5&quot;</option>
                    <option value="3.5">3.5&quot;</option>
                    <option value="5.5">5.5&quot;</option>
                    <option value="6">6&quot;</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-violet-600 mb-1 block">Width</Label>
                  <select value={lbr.width || "6"} onChange={(e) => updateLumber(idx, lbrIdx, "width", e.target.value)}
                    className="w-full h-9 border border-violet-300 bg-white rounded-none text-sm px-1 focus:ring-2 focus:ring-violet-500 focus:outline-none"
                    data-testid={`component-lumber-width-${lbrIdx}-input-${idx}`}>
                    <option value="6">6&quot;</option>
                    <option value="5.5">5.5&quot;</option>
                    <option value="8">8&quot;</option>
                    <option value="Random">Random</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-violet-600 mb-1 block">Length</Label>
                  <select value={lbr.length || "8"} onChange={(e) => updateLumber(idx, lbrIdx, "length", e.target.value)}
                    className="w-full h-9 border border-violet-300 bg-white rounded-none text-sm px-1 focus:ring-2 focus:ring-violet-500 focus:outline-none"
                    data-testid={`component-lumber-length-${lbrIdx}-input-${idx}`}>
                    <option value="">—</option>
                    <option value="2">2&apos; Cutoff</option>
                    <option value="4">4&apos;</option>
                    <option value="5">5&apos;</option>
                    <option value="6">6&apos;</option>
                    <option value="7">7&apos;</option>
                    <option value="8">8&apos;</option>
                    <option value="10">10&apos;</option>
                    <option value="12">12&apos;</option>
                    <option value="14">14&apos;</option>
                    <option value="16">16&apos;</option>
                  </select>
                </div>
              </div>
              {/* Row 2: Pcs/Cant, Species */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-violet-600 mb-1 block">Pcs/Cant</Label>
                  <Input type="number" step="1" min="0" value={lbr.quantity} maxLength={15}
                    onChange={(e) => updateLumber(idx, lbrIdx, "quantity", e.target.value.slice(0, 15))}
                    placeholder="e.g. 27" className="bg-white border border-violet-300 rounded-none shadow-none focus-visible:ring-2 focus-visible:ring-violet-500 text-center"
                    data-testid={`component-lumber-qty-${lbrIdx}-input-${idx}`} />
                </div>
                <div>
                  <Label className="text-xs text-violet-600 mb-1 block">Species</Label>
                  <select
                    value={matchSpecies(lbr.description)}
                    onChange={(e) => { const extra = stripSpecies(lbr.description); const val = e.target.value + (extra ? ' ' + extra : ''); updateLumber(idx, lbrIdx, "description", val.trim()); }}
                    className="w-full h-9 border border-violet-300 bg-white rounded-none text-sm px-1 focus:ring-2 focus:ring-violet-500 focus:outline-none"
                    data-testid={`component-lumber-species-${lbrIdx}-input-${idx}`}>
                    <option value="">Select</option>
                    <option value="Pine">Pine</option>
                    <option value="Southern Yellow Pine">Southern Yellow Pine</option>
                    <option value="Poplar">Poplar</option>
                    <option value="Hardwood">Hardwood</option>
                  </select>
                </div>
              </div>
              {/* Row 3: Description */}
              <div>
                <Label className="text-xs text-violet-600 mb-1 block">Description</Label>
                <Input type="text"
                  value={stripSpecies(lbr.description)}
                  maxLength={30}
                  onChange={(e) => { const species = matchSpecies(lbr.description); const val = species ? (e.target.value ? species + ' ' + e.target.value : species) : e.target.value; updateLumber(idx, lbrIdx, "description", val); }}
                  placeholder="Optional extra description" className="bg-white border border-violet-300 rounded-none shadow-none focus-visible:ring-2 focus-visible:ring-violet-500 w-full"
                  data-testid={`component-lumber-desc-${lbrIdx}-input-${idx}`} />
              </div>
            </div>
          ))}
        </div>
      </div>
      {isStringer && (
        <div className="flex items-center gap-3 mt-3 pt-3 border-t border-zinc-200 flex-wrap">
          <Label className="text-xs text-zinc-500">Notch</Label>
          <div className="flex gap-2">
            <button type="button" onClick={() => updateComponent(idx, "notched", true)}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                comp.notched === true ? "bg-orange-600 text-white" : "bg-white border border-zinc-300 text-zinc-700 hover:bg-zinc-100"
              }`}
              data-testid={`component-notch-yes-${idx}`}>
              <Check className="w-3 h-3 inline mr-1" />Yes
            </button>
            <button type="button" onClick={() => updateComponent(idx, "notched", false)}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                comp.notched === false ? "bg-zinc-700 text-white" : "bg-white border border-zinc-300 text-zinc-700 hover:bg-zinc-100"
              }`}
              data-testid={`component-notch-no-${idx}`}>
              <X className="w-3 h-3 inline mr-1" />No
            </button>
          </div>
          {comp.notched === true && (
            <div className="flex items-center gap-2">
              <Label className="text-xs text-zinc-500 whitespace-nowrap">Length (in)</Label>
              <Input type="number" step="0.125" min="0" value={comp.notch_length}
                onChange={(e) => updateComponent(idx, "notch_length", e.target.value)}
                placeholder="0" className="w-20 bg-white border border-zinc-300 rounded-none shadow-none focus-visible:ring-2 focus-visible:ring-orange-600"
                data-testid={`component-notch-length-${idx}`} />
            </div>
          )}
        </div>
      )}
      <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200">
        <Label className="text-xs font-semibold text-yellow-700 mb-1 block uppercase tracking-wide">Extras</Label>
        <Textarea value={comp.extras || ""} maxLength={300}
          onChange={(e) => updateComponent(idx, "extras", e.target.value)}
          placeholder='e.g. 9/16 spacers... 10" blades... 9 pcs... etc.'
          rows={2}
          className="bg-white border border-yellow-300 rounded-none shadow-none focus-visible:ring-2 focus-visible:ring-yellow-500 text-sm resize-none"
          data-testid={`component-extras-input-${idx}`} />
      </div>
    </div>
  );
}

export function PalletFormDialog({
  isFormOpen, setIsFormOpen, editingPallet, formData, setFormData,
  addComponent, removeComponent, updateComponent, updateLumber,
  handleCreateOrUpdate, loading, onImageUpload, onImageRemove, tutorialMode,
  palletTypes, onDuplicateComponentToPallet,
}) {
  const [duplicatePickerOpen, setDuplicatePickerOpen] = useState(false);
  const [duplicateCompIdx, setDuplicateCompIdx] = useState(null);
  const [duplicateSourcePallet, setDuplicateSourcePallet] = useState(null);
  const [confirmRemoveIdx, setConfirmRemoveIdx] = useState(null);
  const removeFiredRef = useRef(false);
  const [showPhotoOptions, setShowPhotoOptions] = useState(false);

  const handleDuplicateComponent = (compIdx) => {
    setDuplicateCompIdx(compIdx);
    setDuplicatePickerOpen(true);
  };

  const handlePickTarget = (sourcePallet) => {
    setDuplicateSourcePallet(sourcePallet);
  };

  const handlePickSourceComponent = (comp) => {
    if (duplicateCompIdx != null) {
      const copy = JSON.parse(JSON.stringify(comp));
      const comps = [...formData.components];
      comps[duplicateCompIdx] = { ...comps[duplicateCompIdx], ...copy };
      setFormData({ ...formData, components: comps });
    }
    setDuplicatePickerOpen(false);
    setDuplicateSourcePallet(null);
    setDuplicateCompIdx(null);
  };
  const handleFile = (e) => {
    if (e.target.files?.[0]) {
      onImageUpload && onImageUpload(e.target.files[0]);
      setShowPhotoOptions(false);
    }
    e.target.value = '';
  };

  const images = formData.images || (formData.image ? [formData.image] : []);

  return (
    <Dialog open={isFormOpen} onOpenChange={(open) => { if (!open && tutorialMode) return; setIsFormOpen(open); }}>
      <DialogContent
        className="bg-white border border-zinc-200 rounded-none shadow-none w-[calc(100vw-1.5rem)] sm:w-full max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden p-4 sm:p-6"
        data-testid="pallet-form-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-zinc-900 tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
            {editingPallet ? "Edit Pallet Type" : "Create Pallet Type"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {editingPallet ? "Edit pallet type configuration" : "Create a new pallet type configuration"}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label htmlFor="name" className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 mb-2 block">Pallet Name *</Label>
            <Input id="name" value={formData.name} maxLength={130}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Standard 48x40"
              className="bg-white border border-zinc-300 rounded-none shadow-none focus-visible:ring-2 focus-visible:ring-orange-600"
              data-testid="pallet-name-input" />
          </div>
          <div>
            <Label htmlFor="notes" className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 mb-2 block">Notes</Label>
            <NoteArea testId="pallet-notes-input" id="notes" value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Add any notes about this pallet type..."
              rows={3}
              className="bg-white border border-zinc-300 rounded-none shadow-none focus-visible:ring-2 focus-visible:ring-orange-600 resize-none" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 mb-2 block">Photos {images.length > 0 && `(${images.length})`}</Label>
            {images.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mb-2">
                {images.map((img, imgIdx) => (
                  <div key={imgIdx} className="relative group" data-testid={`pallet-image-${imgIdx}`}>
                    <img src={resolveImageUrl(img)} alt={`Pallet ${imgIdx + 1}`} className="w-full h-24 object-cover border border-zinc-200" />
                    <button
                      type="button"
                      onClick={() => onImageRemove(imgIdx)}
                      className="absolute top-1 right-1 p-1 bg-red-600 text-white hover:bg-red-700 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`remove-pallet-image-btn-${imgIdx}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {!showPhotoOptions ? (
              <button type="button" onClick={() => setShowPhotoOptions(true)}
                className="flex items-center justify-center gap-2 w-full p-4 border-2 border-dashed border-zinc-300 cursor-pointer hover:border-orange-400 hover:bg-orange-50 transition-colors"
                data-testid="add-photo-btn">
                <Camera className="w-5 h-5 text-zinc-400" />
                <span className="text-sm text-zinc-500">{images.length > 0 ? 'Add Another Photo' : 'Add Photo'}</span>
              </button>
            ) : (
            <div className="space-y-2 border border-orange-200 p-2 bg-orange-50/40">
              <div className="flex items-center justify-between px-1">
                <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">Choose source</span>
                <button type="button" onClick={() => setShowPhotoOptions(false)} className="p-1 text-zinc-400 hover:text-zinc-600" data-testid="close-photo-options">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <label className="flex items-center justify-center gap-2 w-full p-3 border border-zinc-200 bg-white cursor-pointer hover:border-orange-400 hover:bg-orange-50 transition-colors"
                data-testid="take-photo-btn">
                <Camera className="w-4 h-4 text-zinc-400" />
                <span className="text-xs text-zinc-500">Take photo</span>
                <input type="file" accept="image/*" capture="environment"
                  onChange={handleFile}
                  style={{ width: 0, height: 0, overflow: 'hidden', position: 'absolute' }} />
              </label>
              <label className="flex items-center justify-center gap-2 w-full p-3 border border-zinc-200 bg-white cursor-pointer hover:border-orange-400 hover:bg-orange-50 transition-colors"
                data-testid="choose-gallery-btn">
                <ImageIcon className="w-4 h-4 text-zinc-400" />
                <span className="text-xs text-zinc-500">Choose from gallery</span>
                <input type="file" accept="image/*"
                  onChange={handleFile}
                  style={{ width: 0, height: 0, overflow: 'hidden', position: 'absolute' }} />
              </label>
            </div>
            )}
          </div>
          <Separator className="bg-zinc-200" />
          <div>
            <div className="flex items-center justify-between mb-3">
              <Label className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500">Components *</Label>
              <Button type="button" variant="outline" size="sm" onClick={addComponent}
                disabled={(formData.components || []).length >= 15}
                className="rounded-none border-zinc-300 hover:bg-zinc-100 disabled:opacity-40" data-testid="add-component-btn">
                <Plus className="w-4 h-4 mr-1" />Add Component
              </Button>
            </div>
            <div className="space-y-5 max-h-72 overflow-y-auto">
              {formData.components.map((comp, idx) => (
                <ComponentFormRow
                  key={idx}
                  comp={comp}
                  idx={idx}
                  updateComponent={updateComponent}
                  updateLumber={updateLumber}
                  removeComponent={(i) => { removeFiredRef.current = false; setConfirmRemoveIdx(i); }}
                  canRemove={formData.components.length > 1}
                  onDuplicateComponent={palletTypes?.length > 0 ? handleDuplicateComponent : null}
                />
              ))}
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => setIsFormOpen(false)}
            className="rounded-none border-zinc-300 hover:bg-zinc-100" data-testid="cancel-form-btn">
            Cancel
          </Button>
          <Button onClick={handleCreateOrUpdate} disabled={loading}
            className="bg-orange-600 hover:bg-orange-700 text-white rounded-none font-semibold" data-testid="save-pallet-btn">
            {loading ? "Saving..." : editingPallet ? "Update" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Pallet picker for duplicate component */}
      <Dialog open={duplicatePickerOpen} onOpenChange={(o) => { setDuplicatePickerOpen(o); if (!o) setDuplicateSourcePallet(null); }}>
        <DialogContent className="bg-white border border-zinc-200 rounded-none shadow-none max-w-sm max-h-[70vh] overflow-y-auto" data-testid="duplicate-picker-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-zinc-900 tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
              Duplicate Component From
            </DialogTitle>
            <DialogDescription className="text-sm text-zinc-500">
              {duplicateSourcePallet
                ? `Pick a component from "${duplicateSourcePallet.name}" to copy into Component ${duplicateCompIdx != null ? duplicateCompIdx + 1 : ''}`
                : "Select a pallet type"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1 py-2">
            {duplicateSourcePallet ? (
              <>
                <button onClick={() => setDuplicateSourcePallet(null)}
                  className="text-xs text-zinc-500 hover:text-zinc-700 flex items-center gap-1 mb-1" data-testid="duplicate-back-btn">
                  <ChevronDown className="w-3.5 h-3.5 rotate-90" /> Back to pallet types
                </button>
                {(duplicateSourcePallet.components || []).map((comp, ci) => (
                  <button
                    key={ci}
                    onClick={() => handlePickSourceComponent(comp)}
                    className="w-full text-left px-3 py-3 hover:bg-orange-50 border border-zinc-200 transition-colors flex items-center justify-between group"
                    data-testid={`duplicate-source-comp-${ci}`}
                  >
                    <div>
                      <p className="text-sm font-bold text-zinc-800">{comp.name || `Component ${ci + 1}`}</p>
                      <p className="text-xs text-zinc-400">{comp.quantity || 0} pcs{comp.length ? ` • ${comp.length}"` : ''}</p>
                    </div>
                    <Copy className="w-4 h-4 text-zinc-300 group-hover:text-orange-500 transition-colors" />
                  </button>
                ))}
                {(duplicateSourcePallet.components || []).length === 0 && (
                  <p className="text-sm text-zinc-400 text-center py-4">No components in this pallet type</p>
                )}
              </>
            ) : (
              <>
            {(palletTypes || [])
              .filter(p => !editingPallet || p.id !== editingPallet.id)
              .map((pallet) => (
              <button
                key={pallet.id}
                onClick={() => handlePickTarget(pallet)}
                className="w-full text-left px-3 py-3 hover:bg-orange-50 border border-zinc-200 transition-colors flex items-center justify-between group"
                data-testid={`duplicate-target-${pallet.id}`}
              >
                <div>
                  <p className="text-sm font-bold text-zinc-800">{pallet.name}</p>
                  <p className="text-xs text-zinc-400">{pallet.components?.length || 0} components</p>
                </div>
                <Copy className="w-4 h-4 text-zinc-300 group-hover:text-orange-500 transition-colors" />
              </button>
            ))}
            {(palletTypes || []).filter(p => !editingPallet || p.id !== editingPallet.id).length === 0 && (
              <p className="text-sm text-zinc-400 text-center py-4">No other pallet types available</p>
            )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <AlertDialog open={confirmRemoveIdx !== null} onOpenChange={() => setConfirmRemoveIdx(null)}>
        <AlertDialogContent className="bg-white border border-zinc-200 rounded-none" data-testid="remove-component-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg font-bold text-zinc-900" style={{ fontFamily: "Chivo, sans-serif" }}>
              Remove Component {(confirmRemoveIdx ?? 0) + 1}{formData.components?.[confirmRemoveIdx]?.name ? ` (${formData.components[confirmRemoveIdx].name})` : ""}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-600">
              This removes the component and its lumber options from this pallet type.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-zinc-300" data-testid="remove-component-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              // Guard: a double-tap on Remove fired twice — the 2nd call deleted the
              // component that shifted into the same slot (deleted #3 AND #4)
              if (removeFiredRef.current || confirmRemoveIdx === null) return;
              removeFiredRef.current = true;
              removeComponent(confirmRemoveIdx);
              setConfirmRemoveIdx(null);
            }}
              className="bg-red-600 hover:bg-red-700 text-white rounded-none" data-testid="remove-component-confirm">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}

export function DeleteConfirmDialog({ deleteConfirmId, setDeleteConfirmId, handleDelete }) {
  return (
    <AlertDialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
      <AlertDialogContent className="bg-white border border-zinc-200 rounded-none" data-testid="delete-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-xl font-bold text-zinc-900" style={{ fontFamily: "Chivo, sans-serif" }}>
            Delete Pallet Type?
          </AlertDialogTitle>
          <AlertDialogDescription className="text-zinc-600">
            This action cannot be undone. This will permanently delete the pallet type configuration.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-none border-zinc-300" data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white rounded-none" data-testid="confirm-delete-btn">
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function ClearListDialog({ clearListConfirm, setClearListConfirm, clearList, savedListCount }) {
  return (
    <AlertDialog open={clearListConfirm} onOpenChange={() => setClearListConfirm(false)}>
      <AlertDialogContent className="bg-white border border-zinc-200 rounded-none" data-testid="clear-list-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-xl font-bold text-zinc-900" style={{ fontFamily: "Chivo, sans-serif" }}>
            Clear Entire List?
          </AlertDialogTitle>
          <AlertDialogDescription className="text-zinc-600">
            Are you sure? This will remove {savedListCount} item{savedListCount === 1 ? '' : 's'} from this cut list. Saved-for-later components and your Cut List History are not touched.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-none border-zinc-300" data-testid="cancel-clear-btn">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={clearList} className="bg-red-600 hover:bg-red-700 text-white rounded-none" data-testid="confirm-clear-btn">
            Clear All
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
